import { useEffect, useState, useRef } from 'react'
import { LogIn, LogOut, Loader2 } from 'lucide-react'
import { useSelector } from 'react-redux'
import type { RootState, AppDispatch } from '../store/store'
import { fetchCurrentUser } from '../store/authSlice'
import { useDispatch } from 'react-redux'
import './UserMenu.css'

export function login(returnTo?: string) {
  const params = new URLSearchParams()
  if (returnTo) params.set('returnTo', returnTo)
  window.location.href = `/api/auth-login?${params}`
}

function logout() {
  window.location.href = `/api/auth-logout?returnTo=/`
}

export default function UserMenu() {
  const dispatch = useDispatch<AppDispatch>()
  const { user, loading, checked, unavailable, accessDenied } = useSelector((state: RootState) => state.auth)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!checked) {
      dispatch(fetchCurrentUser())
    }
  }, [dispatch, checked])

  // When the auth check is unavailable (Auth0 throttled/unreachable) keep trying
  // in the background so the session recovers on its own without a forced logout.
  useEffect(() => {
    if (!unavailable) return
    const timer = setInterval(() => dispatch(fetchCurrentUser()), 15000)
    return () => clearInterval(timer)
  }, [dispatch, unavailable])

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setDropdownOpen(false)
      }
    }
    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [dropdownOpen])

  if (loading && !checked) {
    return <div className="user-menu" data-testid="user-menu-loading" />
  }

  // Auth check couldn't reach the identity provider and we don't have a known
  // user to fall back on. Show "reconnecting" instead of Sign In so the user
  // isn't pushed into a redundant OAuth flow while the session is still valid.
  if (!user && unavailable) {
    return (
      <div className="user-menu">
        <div className="user-menu__reconnecting" data-testid="user-menu-reconnecting" role="status">
          <Loader2 size={16} className="user-menu__spinner" aria-hidden="true" />
          Reconnecting…
        </div>
      </div>
    )
  }

  if (!user && accessDenied) {
    return (
      <div className="user-menu">
        <span className="user-menu__access-denied" data-testid="user-menu-access-denied" role="status">
          LoopBuilder requires a verified @replay.io account
        </span>
        <button
          className="user-menu__sign-in"
          data-testid="sign-out-button"
          onClick={() => logout()}
        >
          <LogOut size={16} />
          Sign Out
        </button>
      </div>
    )
  }

  if (!user) {
    return (
      <div className="user-menu">
        <button
          className="user-menu__sign-in"
          data-testid="sign-in-button"
          onClick={() => login(window.location.pathname)}
        >
          <LogIn size={16} />
          Sign In
        </button>
      </div>
    )
  }

  const initials = user.name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)

  return (
    <div className="user-menu" data-testid="user-menu">
      <div className="user-menu__container" ref={containerRef}>
        <button
          className="user-menu__trigger"
          data-testid="user-menu-trigger"
          onClick={() => setDropdownOpen(!dropdownOpen)}
        >
          {user.picture ? (
            <img src={user.picture} alt="" className="user-menu__avatar" />
          ) : (
            <div className="user-menu__avatar-fallback">{initials}</div>
          )}
          <span className="user-menu__name">{user.name}</span>
        </button>
        {dropdownOpen && (
          <div className="user-menu__dropdown" data-testid="user-menu-dropdown">
            <div className="user-menu__dropdown-info">
              <div className="user-menu__dropdown-name">{user.name}</div>
              {user.email && (
                <div className="user-menu__dropdown-email">{user.email}</div>
              )}
            </div>
            <button
              className="user-menu__dropdown-item"
              data-testid="sign-out-button"
              onClick={() => logout()}
            >
              <LogOut size={16} />
              Sign Out
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
